import { create } from "zustand";

export type ThemeMode = "light" | "dark" | "system";

type UIState = {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;

  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;

  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
};

const THEME_KEY = "staxed-theme";

function readStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const raw = window.localStorage.getItem(THEME_KEY);
  if (raw === "light" || raw === "dark" || raw === "system") return raw;
  return "system";
}

function applyTheme(theme: ThemeMode): void {
  if (typeof document === "undefined") return;
  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDark = theme === "dark" || (theme === "system" && prefersDark);
  document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
}

export const useUIStore = create<UIState>((set) => ({
  theme: readStoredTheme(),
  setTheme: (theme) => {
    window.localStorage.setItem(THEME_KEY, theme);
    applyTheme(theme);
    set({ theme });
  },

  chatOpen: false,
  setChatOpen: (chatOpen) => set({ chatOpen }),

  mobileNavOpen: false,
  setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),
}));

// Listen to system theme changes; if user picked "system", re-apply.
if (typeof window !== "undefined" && window.matchMedia) {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (useUIStore.getState().theme === "system") {
      applyTheme("system");
    }
  });
}
